/**
 * TagCheckboxes — checkbox group of all tags for the client create/edit form.
 *
 * Selection is controlled by the parent; `syncClientTags` diffs initial vs
 * final selection against the existing assign/unassign endpoints on submit.
 */

import { useQuery } from '@tanstack/react-query';
import type { TagOutput } from '@1financial/shared';
import { getAccessToken, authenticatedFetch, captureAuthenticatedFetch } from '../lib/auth-context.js';
import { httpError } from '../lib/http-error.js';

export function TagCheckboxes({ selected, onChange }: { selected: Set<string>; onChange: (next: Set<string>) => void }) {
  const list = useQuery({
    queryKey: ['tags'],
    queryFn: async () => {
      const res = await authenticatedFetch('/v1/tags?limit=100', { headers: { Authorization: `Bearer ${getAccessToken() ?? ''}` } });
      if (!res.ok) throw await httpError(res);
      const body = await res.json() as { ok: true; data: { items: TagOutput[] } };
      return body.data.items;
    },
  });

  function toggle(id: string) {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    onChange(next);
  }

  if (list.isLoading) return <p className="text-xs text-muted">Loading tags…</p>;
  if (list.error) return <p className="text-xs text-rose-400">{(list.error as Error).message}</p>;
  if (!list.data || list.data.length === 0) return <p className="text-xs text-muted">No tags yet — create them on the Tags page.</p>;

  return (
    <div className="flex flex-wrap gap-2">
      {list.data.map((t) => {
        const checked = selected.has(t.id);
        return (
          <label
            key={t.id}
            className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md border text-xs font-medium cursor-pointer select-none transition-colors"
            style={{
              borderColor: t.color,
              color: checked ? t.color : '#8b8ba0',
              backgroundColor: checked ? `${t.color}1a` : 'transparent',
            }}
          >
            <input
              type="checkbox"
              checked={checked}
              onChange={() => toggle(t.id)}
              className="w-3.5 h-3.5 accent-emerald-500 cursor-pointer"
            />
            {t.name}
          </label>
        );
      })}
    </div>
  );
}

/** Apply the selection diff for one client via the existing tag endpoints. */
export async function syncClientTags(clientId: string, initial: Set<string>, next: Set<string>): Promise<void> {
  const send = captureAuthenticatedFetch();
  const postTagOp = async (path: string, tagId: string) => {
    const res = await send(path, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ tagId, scope: 'CLIENT', clientId }) });
    if (!res.ok) throw await httpError(res);
  };
  for (const tagId of next) {
    if (!initial.has(tagId)) await postTagOp('/v1/tags/assign', tagId);
  }
  for (const tagId of initial) {
    if (!next.has(tagId)) await postTagOp('/v1/tags/unassign', tagId);
  }
}
